// Progressive editorial layout. The browser parses HTML; Liquid never splits tags.
// All source wording is server rendered and remains readable if enhancement fails.
const cleanText = node => node.textContent.replace(/\s+/g, ' ').trim();
const make = (doc, tag, className) => {
  const node = doc.createElement(tag);
  if (className) node.className = className;
  return node;
};

function normalize(source, page, sourceLabel) {
  const studies = page === 'studien';
  for (const node of source.querySelectorAll('*')) {
    for (const name of ['style', 'class', 'data-start', 'data-end', 'data-section-id']) node.removeAttribute(name);
  }
  for (const svg of source.querySelectorAll('svg[aria-hidden="true"]')) svg.remove();
  for (const wrapper of [...source.querySelectorAll('div,span')].reverse()) {
    // Keep semantic attributes and fragment targets; Range handles their nesting.
    if (!wrapper.attributes.length) wrapper.replaceWith(...wrapper.childNodes);
  }
  for (const heading of source.querySelectorAll('h1')) {
    const replacement = make(source.ownerDocument, 'h2');
    for (const attr of heading.attributes) replacement.setAttribute(attr.name, attr.value);
    replacement.append(...heading.childNodes);
    heading.replaceWith(replacement);
  }
  if (studies) {
    for (const paragraph of source.querySelectorAll('p')) {
      const text = cleanText(paragraph);
      const tag = text === 'Warum ohne Tenside (SLS)?' ? 'h2' : text === 'Unser Ansatz' ? 'h3' : '';
      if (!tag || paragraph.children.length !== 1 || paragraph.firstElementChild.tagName !== 'STRONG') continue;
      const heading = make(source.ownerDocument, tag);
      for (const attr of paragraph.attributes) heading.setAttribute(attr.name, attr.value);
      heading.append(...paragraph.childNodes);
      paragraph.replaceWith(heading);
    }
  }
  for (const heading of source.querySelectorAll('h2')) if (!cleanText(heading)) heading.remove();
  if (page === 'inhaltsstoffe') {
    const firstHeading = source.querySelector('h2');
    // Retain the previous presentation correction for the duplicate editor label.
    for (const paragraph of source.querySelectorAll('p')) {
      if (cleanText(paragraph) === 'Inhaltsstoffe' && firstHeading && paragraph.compareDocumentPosition(firstHeading) & 4) paragraph.remove();
    }
  }
  for (const link of source.querySelectorAll('a[href]')) {
    if (cleanText(link) || link.querySelector('img,video,svg:not([aria-hidden="true"])')) continue;
    let host = '';
    try { host = new URL(link.getAttribute('href'), source.ownerDocument.baseURI).hostname; } catch { /* Keep original href. */ }
    link.append(source.ownerDocument.createTextNode(link.getAttribute('aria-label') || link.title || `${sourceLabel}${host ? ` (${host})` : ''}`));
  }
}

function slice(source, after, before) {
  const range = source.ownerDocument.createRange();
  range.selectNodeContents(source);
  if (after) range.setStartAfter(after);
  if (before) range.setEndBefore(before);
  // cloneContents preserves balanced semantic ancestors across heading boundaries.
  return range.cloneContents();
}

export function enhanceInformationPage(article) {
  if (article.dataset.brandEnhanced) return;
  const original = article.querySelector('[data-brand-source]');
  const leadContainer = article.querySelector('[data-brand-lead]');
  const chapterContainer = article.querySelector('[data-brand-chapters]');
  const nav = article.querySelector('[data-brand-links]');
  if (!original || !leadContainer || !chapterContainer || !nav) return;
  const source = original.cloneNode(true);
  const doc = article.ownerDocument;
  const about = article.dataset.brandPage === 'uber-uns';
  const studies = article.dataset.brandPage === 'studien';
  normalize(source, article.dataset.brandPage, article.dataset.sourceLabel || 'Quelle öffnen');
  const headings = [...source.querySelectorAll('h2')];
  // Recognize the existing study deck by content. New leading paragraphs survive.
  const studyDeck = studies && cleanText(headings[0] || source) === 'Warum Freyacare ohne Fluorid, Titandioxid, Tenside und Glycerin arbeitet'
    && cleanText(headings[1] || source) === 'Natürlich. Basisch. Wissenschaftlich gedacht.';
  const offset = studyDeck ? 2 : 0;
  const intro = slice(source, null, headings[offset] || null);
  if (studyDeck) {
    const tagline = intro.querySelectorAll('h2')[1];
    const paragraph = make(doc, 'p', 'brand-study-tagline');
    for (const attr of tagline.attributes) if (attr.name !== 'class') paragraph.setAttribute(attr.name, attr.value);
    paragraph.append(...tagline.childNodes);
    tagline.replaceWith(paragraph);
  }
  const usedIDs = new Set([...doc.querySelectorAll('[id]')].filter(node => !original.contains(node)).map(node => node.id));
  const reserveIDs = fragment => {
    for (const node of fragment.querySelectorAll('[id]')) {
      if (usedIDs.has(node.id)) node.removeAttribute('id');
      else usedIDs.add(node.id);
    }
  };
  reserveIDs(intro);
  const chapters = doc.createDocumentFragment();
  const links = doc.createDocumentFragment();
  const arrow = article.querySelector('[data-brand-arrow]');
  const motifs = new Map([['Xylit (Birkenzucker)', 'xylit'], ['Pfefferminzöl (Mentha Piperita)', 'mint'], ['Salbeiöl (Salvia Officinalis)', 'sage']]);
  const chapterHeadings = headings.slice(offset);
  chapterHeadings.forEach((heading, index) => {
    const closing = index === chapterHeadings.length - 1;
    const section = make(doc, 'section', `brand-chapter${closing ? ' brand-chapter--closing' : ''}${about && index === 1 || studies && closing ? ' brand-chapter--soft' : ''}`);
    const title = heading.cloneNode(true);
    for (const br of title.querySelectorAll('br')) br.replaceWith(doc.createTextNode(' '));
    if (!title.id || usedIDs.has(title.id)) {
      const base = `information-${article.dataset.sectionKey}-${index + 1}`;
      let id = base, suffix = 1;
      while (usedIDs.has(id)) id = `${base}-${suffix++}`;
      title.id = id;
    }
    const body = slice(source, heading, chapterHeadings[index + 1] || null);
    const headingBox = make(doc, 'div', 'brand-chapter-heading');
    headingBox.append(title);
    reserveIDs(headingBox);
    reserveIDs(body);
    section.setAttribute('aria-labelledby', title.id);
    if (!closing) {
      const number = make(doc, 'span', 'eyebrow brand-chapter-number');
      number.setAttribute('aria-hidden', 'true');
      number.textContent = String(index + 1).padStart(2, '0');
      headingBox.prepend(number);
    }
    const contentBox = make(doc, 'div', 'brand-chapter-content');
    const prose = make(doc, 'div', 'prose');
    prose.append(body);
    const motif = !about && !studies && motifs.get(cleanText(title));
    if (motif) {
      const image = make(doc, 'img', 'brand-ingredient-image');
      image.src = article.dataset[`ingredient${motif[0].toUpperCase()}${motif.slice(1)}`];
      image.alt = ''; image.width = 360; image.height = 360; image.loading = 'lazy'; image.decoding = 'async';
      contentBox.append(image);
      section.classList.add('brand-chapter--illustrated');
    }
    contentBox.append(prose);
    section.append(headingBox, contentBox);
    chapters.append(section);
    if (!(about && closing)) {
      const link = make(doc, 'a');
      link.setAttribute('href', `#${title.id}`);
      link.textContent = cleanText(title);
      if (arrow) link.append(doc.createTextNode(' '), arrow.content.cloneNode(true));
      links.append(link);
    }
  });
  // Commit only after the detached enhancement is complete. Never hide source first.
  leadContainer.replaceChildren(intro);
  chapterContainer.replaceChildren(chapters);
  nav.replaceChildren(links);
  nav.hidden = !nav.childElementCount;
  const index = article.querySelector('[data-brand-index]');
  if (index) index.hidden = !nav.childElementCount;
  original.remove();
  article.dataset.brandEnhanced = 'true';
}

function initialize(root = document) {
  for (const article of root.querySelectorAll('[data-brand-page]')) {
    try { enhanceInformationPage(article); } catch (error) {
      // The server-rendered content remains readable on unsupported/malformed input.
      console.warn('Freyacare editorial enhancement unavailable', error);
    }
  }
}
initialize();
document.addEventListener('shopify:section:load', event => initialize(event.target));
